<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledSearchInput from "@/components/StyledSearchInput.vue";
import useFuse from "@/composables/useFuse";
import removeStringSpaces from "@/helpers/removeStringSpaces";
import MemberEntry from "@/modules/roles/components/MemberEntry.vue";
import useRoleUsers from "@/modules/roles/composables/useRoleUsers";
import { Role } from "@/modules/roles/composables/useRoles";
import { User } from "@/modules/users/interfaces/user";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, ref } from "vue";
import { Collapse } from "vue-collapsed";
import { useToast } from "vue-toastification";

interface Props {
  role: Role;
}

const props = defineProps<Props>();
const toast = useToast();
const queryClient = useQueryClient();
const searchQuery = ref("");

const {
  roleUsers,
  isLoading,
  users,
  setQuery,
  loadingUsers,
  roleUsersMutationAsync,
} = useRoleUsers({
  roleId: props.role.id,
  company: props.role.companyId,
  immediate: true,
});

const filteredMembers = computed(() => {
  const users = roleUsers.value;

  if (!searchQuery.value) return users;

  const fuse = useFuse(users, {
    keys: ["fullName"],
    threshold: 0.4,
    ignoreLocation: true,
  });

  return fuse
    .search(removeStringSpaces(searchQuery.value))
    .map((result) => result.item);
});

const collapse = ref(false);

const handleCollapse = () => {
  collapse.value = !collapse.value;
};

const form = reactive({
  users: [] as User[],
});

const handleAddUsers = async () => {
  const userFind = roleUsers.value.find((user) =>
    form.users.some((u) => u.id === user.id),
  );

  if (userFind) {
    toast.error(`El usuario: ${userFind.fullName} ya pertenece a este rol`);
    return;
  }

  const users = form.users
    .map((user) => user.id)
    .concat(roleUsers.value.map((user) => user.id));

  await roleUsersMutationAsync(users);

  await queryClient.invalidateQueries(["roleUsers", props.role.id]);

  form.users = [];
  handleCollapse();
};

const handleDeleteUser = async (id: number) => {
  const usersFiltered = roleUsers.value.filter((user) => user.id !== id);

  await roleUsersMutationAsync(usersFiltered.map((user) => user.id));

  await queryClient.invalidateQueries(["roleUsers", props.role.id]);

  toast.success("Usuario removido del rol correctamente");
};
</script>

<template>
  <div class="">
    <div class="flex justify-between items-center mb-2">
      <p v-if="!collapse" class="text-base">
        Mostrando {{ roleUsers.length }} usuarios
      </p>

      <div v-if="role.name !== 'Master'">
        <HasPermission
          name-permission="update-roles"
          :entity-type="'App\\Models\\Company'"
        >
          <StyledButton
            label="Agregar"
            class="bg-blue-600 text-white"
            @click="handleCollapse()"
            v-if="!collapse"
          />
        </HasPermission>
      </div>
    </div>

    <Collapse :when="collapse">
      <StyledCombobox
        multiple
        v-model="form.users"
        label="Agregar miembros"
        placeholder="Buscar miembros"
        :options="users || []"
        :loading-options="loadingUsers"
        :display-value="(value) => value.fullName"
        :on-input-change="setQuery"
        :on-blur="() => setQuery('')"
      >
        <template v-slot="{ option }">
          <span>{{
            `${option.name} ${option.lastname} ${option.email ? "| " + `${option.email}` : ""} `
          }}</span>
        </template>
      </StyledCombobox>

      <div class="p-2">
        <h2 class="font-bold text-sm">Usuarios Seleccionados:</h2>
        <div v-if="form.users.length" class="flex flex-wrap gap-2 mb-2">
          <div
            v-for="user in form.users"
            :key="user.id"
            class="flex items-center gap-2"
          >
            <span
              class="bg-slate-200 hover:bg-gray-300 p-2 text-black rounded-lg flex items-center gap-2"
              v-tooltip="`${user.email ?? 'No tiene correo'}`"
            >
              <span
                @click="form.users = form.users.filter((u) => u.id !== user.id)"
                class="material-symbols-outlined text-sm cursor-pointer text-gray-400 hover:text-red-500 transition-colors"
              >
                close
              </span>

              {{ `${user.name} ${user.lastname} ` }}
            </span>
          </div>
        </div>

        <div class="flex flex-row gap-2 mt-2 mb-2 justify-end">
          <div>
            <StyledButton
              @click="handleAddUsers"
              :disabled="!form.users.length"
              label="Agregar"
              :class="[
                'text-white ',
                form.users.length
                  ? 'bg-blue-500 hover:bg-blue-700'
                  : 'bg-blue-300 hover:bg-blue-400',
              ]"
            />
          </div>

          <div>
            <StyledButton
              @click="handleCollapse"
              label="Cancelar"
              class="text-white bg-red-600 hover:bg-red-700"
            />
          </div>
        </div>
      </div>
    </Collapse>

    <StyledSearchInput
      v-model="searchQuery"
      placeholder="Buscar miembros"
      class="w-full mt-2"
    />

    <div class="grid gap-3">
      <div v-if="!isLoading" class="mt-3">
        <template v-if="filteredMembers.length">
          <MemberEntry
            :permission-to-assign="'update-roles'"
            :module="'App\\Models\\Company'"
            v-for="member in filteredMembers"
            :key="member.id"
            :member="member"
            :role="role"
            @remove-member="handleDeleteUser"
          />
        </template>

        <div v-else>
          <p>No hay miembros en este rol</p>
        </div>
      </div>
    </div>

    <LoadingModal :when="isLoading" />
  </div>
</template>

<style scoped></style>
